<template>
  <m-dropdown
    ref="dropdown"
    v-model:value="showMenu"
    :match-trigger-width="matchTriggerWidth"
    :class="['m-select', small ? '-small' : '', xs ? '-xs' : '']"
    :placement="popup ? 'onTopLeft' : 'bottomCenter'"
    :disabled="disabled || readOnly || loading"
    :title="title"
    :block="fullWidth"
    @hide="hide"
    @keydown="handleKeyDown"
  >
    <slot name="trigger">
      <m-focusable
        ref="trigger"
        :hide-border="hideBorder"
        class="_trigger"
        type="clickable"
        :has-error="hasError"
        :placeholder="placeholder === '' ? $t('general.empty') : placeholder"
        :placeholder-icon="placeholderIcon"
        :show-placeholder="valueIsEmpty && !hidePlaceholder"
        :hide-hover="hideHover"
        :small="small"
        :xs="xs"
        :disabled="disabled"
        :read-only="readOnly"
        :full-width="fullWidth"
        light
        :m-style="mStyle"
        :wrap="!nowrap"
        :no-padding="noPadding"
        has-tags
        tabindex="0"
        @keydown.enter="show"
        @click="show"
      >
        <div
          v-if="loaded"
          :class="['_value', fullWidth ? '-full-width' : '']"
        >
          <span
            v-if="label !== ''"
            class="_label"
          >
            {{ label }}
          </span>
          <m-tag-list
            :small="small"
            :xs="xs"
            :layout="!nowrap ? 'wrap' : 'inline'"
          >
            <template
              v-for="(v, j) in iterableValue"
            >
              <template
                v-if="j < truncateSelectedItems || truncateSelectedItems === -1"
              >
                <m-tag
                  :key="j"
                  :color="triggerColor(v)"
                  :custom-color="triggerCustomColor(v)"
                  :automatic-color-fallback="automaticColor"
                  :title="getLabel(v)"
                  :icon="getOption(v) === null ? '' : getOption(v).icon"
                  :style="triggerStyle"
                  :m-style="{ ...defaultMStyle, ...mStyle }"
                  :small="!small && !xs"
                  :xs="small"
                  :xxs="xs"
                >
                  <slot
                    v-if="hasCustomItem"
                    :item="v"
                    :is-trigger="true"
                    :index="j"
                    name="item"
                  />
                </m-tag>
              </template>
            </template>
            <template v-if="iterableValue.length > truncateSelectedItems && truncateSelectedItems !== -1">
              <m-tag
                :color="(!tags && !multiple) || hasCustomItem ? 'none' : (light ? 'light' : 'default')"
                :title="`+${iterableValue.length - truncateSelectedItems} ${$t('mSelect.more', iterableValue.length - truncateSelectedItems)}`"
                :style="triggerStyle"
                :m-style="{ ...defaultMStyle, ...mStyle }"
                :small="!small && !xs"
                :xs="small"
                :xxs="xs"
              />
            </template>
          </m-tag-list>
        </div>
        <template
          v-if="!hideArrow && !disabled && !readOnly"
          #suffix
        >
          <m-icon
            class="_icon"
            :size="iconSize"
            :color="$colors.grey.lighten1"
            :type="loading ? 'loading': arrowType"
            :spin="loading"
          />
        </template>
      </m-focusable>
    </slot>
    <template #overlay>
      <slot name="overlay">
        <m-card
          :has-header="popup"
          :style="overlayStyle"
          :class="['_m-select-overlay', $store.state.breakpoint.smAndDown ? '-mobile' : '']"
          no-padding
          tabindex="-1"
          :border-radius="popup ? 'small' : 'default'"
        >
          <slot name="overlay-header" />
          <div :class="['_input-field', popup ? '-popup' : '', tags ? '-tags' : '', showSearch ? '-search' : '']">
            <m-focusable
              v-if="popup || tags"
              :hide-border="popup"
              :hide-hover="popup"
              has-tags
              :full-width="fullWidth"
              class="_input"
            >
              <m-tag-list
                layout="wrap"
              >
                <template
                  v-for="(v, j) in iterableValue"
                  :key="j"
                >
                  <m-tag
                    :title="!hasCustomItem ? getLabel(v) : ''"
                    :icon="getOption(v) === null ? '' : getOption(v).icon"
                    :color="(!tags && !multiple) || hasCustomItem ? 'none' : v.color"
                    :automatic-color-fallback="automaticColor"
                    :style="triggerStyle"
                    small
                    :closeable="clearable || multiple"
                    @close="deselect(getOption(v))"
                  >
                    <slot
                      v-if="hasCustomItem"
                      :item="v"
                      :is-trigger="true"
                      name="item"
                    />
                  </m-tag>
                </template>
                <div class="_search">
                  <m-text-field
                    ref="search"
                    v-model:value="search"
                    class="_search-input"
                    :placeholder="valueIsEmpty ? emptyPlaceholder : ''"
                    inherit-styling
                    auto-focus
                    @keydown="handleSearchKeyDown"
                    @input="changeSearch"
                  />
                </div>
              </m-tag-list>
            </m-focusable>
            <m-text-field
              v-else-if="showSearch"
              v-model:value="search"
              class="_m-select-search"
              :placeholder="$t('mSelect.searchPlaceholder')"
              full-width
              auto-focus
              has-background
              @input="changeSearch"
              @blur="focusDropdown"
            />
          </div>
          <div
            ref="body"
            class="_body"
          >
            <template v-if="showDescription">
              <m-card-item
                :clickable="false"
                :color="$colors.grey.lighten1"
                no-hover
                class="_sub-heading"
              >
                <template v-if="description === ''">
                  {{ $t('mSelect.pickAnItem') }}
                </template>
                <template v-else>
                  {{ description }}
                </template>
              </m-card-item>
            </template>
            <m-card-item
              v-if="filteredOptions.length === 0"
              :clickable="false"
              no-hover
              class="_no-items"
              light
            >
              {{ $t('mSelect.noItems') }}
            </m-card-item>
            <m-endless-scroll-list @visible="loadMore">
              <template
                v-for="(item, index) in list"
                :key="item.key"
              >
                <sub-heading
                  v-if="groups[index] !== undefined && search === ''"
                  :title="groups[index].label"
                />
                <m-select-item
                  :data-key="item.key"
                  :item="item"
                  :selected="isSelected(item)"
                  :focused="index === focusedItem"
                  :automatic-color="automaticColor"
                  :tags="tags"
                  :has-tooltip="hasTooltip"
                  :tooltip-placement="tooltipPlacement"
                  @click="select(item)"
                  @focus="focusedItem = index"
                >
                  <template
                    v-if="hasCustomItem"
                    #default
                  >
                    <slot
                      name="item"
                      :item="item.value"
                    />
                  </template>
                  <template
                    v-if="hasTooltip"
                    #tooltip
                  >
                    <slot
                      name="item-tooltip"
                      :item="item"
                    >
                      {{ item.item.tooltip }}
                    </slot>
                  </template>
                </m-select-item>
              </template>
            </m-endless-scroll-list>
          </div>
        </m-card>
      </slot>
    </template>
  </m-dropdown>
</template>

<script>
import MEndlessScrollList from 'shared/components/base/MEndlessScrollList.vue';
import MSelectItem from 'shared/components/base/MSelectItem.vue';
import MTextField from 'shared/components/base/MTextField.vue';
import SubHeading from 'shared/components/SubHeading.vue';
import stringify from 'fast-json-stable-stringify';
import { copy } from 'shared/lib/copy';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { mStyleProps, resolveStyles } from 'shared/lib/m-style-props';
import { uniqBy } from 'lodash-es';

const itemsPerPage = 20;

export default {
  name: 'MSelect',
  props: {
    ...mStyleProps,
    open: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => ([]),
    },
    groupedItems: {
      type: Array,
      default: () => ([]),
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    superLight: {
      type: Boolean,
      default: false,
    },
    showDescription: {
      type: Boolean,
      default: false,
    },
    description: {
      type: String,
      default: '',
    },
    tags: {
      type: Boolean,
      default: false,
    },
    hideSelectedValues: {
      type: Boolean,
      default: false,
    },
    matchTriggerWidth: {
      type: Boolean,
      default: false,
    },
    hideHover: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Object, Array, Number, Boolean],
      default: () => [],
    },
    small: {
      type: Boolean,
      default: false,
    },
    xs: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
    maxTagTextLength: {
      type: Number,
      default: 0,
    },
    iconSize: {
      type: String,
      default: '11',
    },
    valueKey: {
      type: String,
      default: 'value',
    },
    optionKey: {
      type: String,
      default: '',
    },
    itemText: {
      type: String,
      default: 'text',
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    filterFunction: {
      type: Function,
      default: (input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    popup: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    hideBorder: {
      type: Boolean,
      default: false,
    },
    hideArrow: {
      type: Boolean,
      default: false,
    },
    arrowType: {
      type: String,
      default: 'down',
    },
    automaticColor: {
      type: Boolean,
      default: false,
    },
    cacheItems: {
      type: Boolean,
      default: false,
    },
    placeholderIcon: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    hidePlaceholder: {
      type: Boolean,
      default: false,
    },
    keepOpenOnClick: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    triggerStyle: {
      type: Object,
      default: () => ({}),
    },
    label: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    tooltipPlacement: {
      type: String,
      default: 'top',
    },
    truncateSelectedItems: {
      type: Number,
      default: -1,
    },
    nowrap: {
      type: Boolean,
      default: false,
    },
    hasTooltip: {
      type: Boolean,
      default: false,
    },
    // If true, the localValue will be controlled by the parent component
    controlled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:open',
    'input',
    'update:value',
    'change',
    'search',
    'close',
    'item-click',
  ],
  components: { SubHeading, MEndlessScrollList, MTextField, MSelectItem },
  data() {
    return {
      localValue: [],
      showMenu: false,
      search: '',
      focusedItem: 0,
      localItems: [],
      loaded: false,
      amountOfItems: itemsPerPage,
      defaultMStyle: { fontStyle: { fontSize: 'regular' } },
    };
  },
  computed: {
    hasCustomItem() {
      return !!this.$slots.item;
    },
    title() {
      if (this.description !== '') {
        return this.description;
      }

      return this.$t('mSelect.title');
    },
    emptyPlaceholder() {
      if (this.hidePlaceholder) {
        return '';
      }

      if (this.placeholder === '') {
        return this.$t('mSelect.placeholder');
      }

      return this.placeholder;
    },
    iterableValue() {
      if (Array.isArray(this.localValue)) {
        return this.localValue;
      }

      if (this.localValue === null) {
        return [];
      }

      if (this.localValue === '') {
        return [];
      }

      return [this.localValue];
    },
    overlayStyle() {
      const style = {};
      if (!this.matchTriggerWidth) {
        if (this.$store.state.breakpoint.smAndDown) {
          style.maxWidth = '100vw';
        } else {
          style.maxWidth = '40rem';
        }
      }

      return style;
    },
    valueIsEmpty() {
      if (this.val === null) {
        return true;
      }

      return this.val.length === 0;
    },
    val() {
      return this.getValue(this.localValue);
    },
    list() {
      return this.filteredOptions.slice(0, this.amountOfItems);
    },
    filteredOptions() {
      const filteredOptions = this.options.filter((o) => {
        if (typeof o.item === 'undefined') {
          return true;
        }

        if (typeof o.item.disabled === 'undefined') {
          return true;
        }

        return !o.item.disabled;
      }).filter((o) => this.filterFunction(this.search, o));
      if (this.hideSelectedValues) {
        return filteredOptions
          .filter((o) => !this.keyIsSelected(o.key));
      }
      return filteredOptions;
    },
    options() {
      return this.localItems.map((item) => this.createOption(item));
    },
    groups() {
      if (this.groupedItems.length === 0) {
        return {};
      }

      let counter = 0;
      return this.groupedItems.reduce((res, next) => {
        if (next.options === undefined) {
          return res;
        }
        res[counter] = { label: next.label };
        counter += next.options.length;
        return res;
      }, {});
    },
    calculatedItems() {
      if (this.groupedItems.length === 0) {
        return this.items;
      }

      return this.groupedItems.reduce((res, next) => {
        if (next.options === undefined) {
          res.push(next);
          return res;
        }
        res.push(...next.options);
        return res;
      }, []);
    },
  },
  methods: {
    resolveStyles,
    loadMore() {
      this.amountOfItems += itemsPerPage;
    },
    hide() {
      this.amountOfItems = itemsPerPage;
      this.showMenu = false;
      this.$refs.trigger?.$el.focus();
      this.$emit('close');
    },
    show() {
      if (this.disabled || this.loading) {
        return;
      }

      this.showMenu = true;
    },
    focusDropdown() {
      this.$refs.dropdown?.focus();
    },
    clear() {
      const clearedValue = this.multiple ? [] : null;
      if (!this.controlled) {
        this.localValue = clearedValue;
      }
      this.$emit('input', clearedValue);
      this.$emit('update:value', clearedValue);
      this.$emit('change', clearedValue);
    },
    handleSearchKeyDown(event) {
      if (event.key !== 'Backspace' || this.search !== '') {
        return;
      }
      if (this.multiple) {
        if (this.localValue.length === 0) {
          return;
        }
        const item = this.getOption(
          this.localValue[this.localValue.length - 1],
        );
        if (item === null) {
          return;
        }

        this.select(item);
        return;
      }

      if (this.localValue === null) {
        return;
      }
      this.deselect(this.getOption(this.localValue));
    },
    triggerColor(v) {
      if (this.hasCustomItem || (!this.tags && !this.multiple)) {
        return 'none';
      }

      const option = this.getOption(v);
      if (isNullOrUndefined(option)) {
        return 'none';
      }
      return option.item.color;
    },
    triggerCustomColor(v) {
      if (this.hasCustomItem) {
        return undefined;
      }

      if (!this.tags && !this.multiple) {
        if (this.light) {
          return { color: this.$colors.grey.base };
        }
        if (this.superLight) {
          return { color: this.$colors.grey.lighten1 };
        }
        return undefined;
      }

      return this.getOption(v)?.item?.customColor;
    },
    getOption(value) {
      if (value === null) {
        return null;
      }

      if (this.returnObject) {
        const filtered = this.options.filter((o) => this.equalP(o.value, value[this.valueKey]));
        if (filtered.length !== 1) {
          return null;
        }

        return filtered[0];
      }

      const filtered = this.options.filter((o) => this.equalP(o.value, value));
      if (filtered.length !== 1) {
        return null;
      }

      return filtered[0];
    },
    base64(obj) {
      if (typeof obj === 'undefined' || obj === null) {
        return -1;
      }

      return btoa(stringify(obj));
    },
    equalP(a, b) {
      if (typeof a === 'number' && typeof b === 'number') {
        return a === b;
      }

      if (typeof a === 'string' && typeof b === 'string') {
        return a === b;
      }

      return this.base64(a) === this.base64(b);
    },
    getLabel(value) {
      const option = this.getOption(value);
      if (option === null) {
        return '';
      }

      if (
        this.maxTagTextLength !== 0
        && option.label.length > this.maxTagTextLength
      ) {
        return `${option.label.slice(0, this.maxTagTextLength)}...`;
      }

      return option.label;
    },
    createOption(item) {
      if (!(item instanceof Object)) {
        return {
          label: item,
          value: item,
          key: this.base64(item),
          selectable: true,
          text: item,
          icon: '',
          color: '',
        };
      }

      const optionKey = this.optionKey === '' ? this.valueKey : this.optionKey;
      return {
        label: this.byString(item, this.itemText),
        value: item[this.valueKey],
        key: this.base64(item[optionKey]),
        selectable: item.selectable === undefined ? true : item.selectable,
        icon: item.icon,
        color: item.color,
        item,
      };
    },
    selectFocusedItem() {
      this.select(this.filteredOptions[this.focusedItem]);
    },
    deselect(item) {
      if (!this.multiple) {
        this.clear();
        return;
      }

      this.select(item);
    },
    select(item) {
      this.emitItemClick(item.key);

      if (item.selectable) {
        this.change(item.key);
      }
      this.search = '';

      if (this.keepOpenOnClick || this.multiple) {
        return;
      }
      this.hide();
    },
    isSelected(item) {
      return this.keyIsSelected(item.key);
    },
    valueByKey(key) {
      const filtered = this.options.filter((i) => i.key === key);
      if (filtered.length === 0) {
        return null;
      }
      return filtered[0];
    },
    change(key) {
      this.updateVal(key);
    },
    changeSearch(value) {
      this.search = value;
      this.$emit('search', this.search);
    },
    byString(o, s) {
      const str = s.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '');
      const a = str.split('.');
      let obj = o;
      for (let i = 0, n = a.length; i < n; ++i) {
        const k = a[i];
        if (k in obj) {
          obj = obj[k];
        }
      }

      return obj;
    },
    getValue(values) {
      const getVal = (value) => {
        let valueObject = value;
        if (this.returnObject && value !== null) {
          valueObject = value[this.valueKey];
        }

        if (this.returnObject && value === null) {
          valueObject = null;
        }

        const val = this.valueByKey(this.base64(valueObject));
        if (val === null) {
          return null;
        }
        return val.key;
      };

      if (!this.multiple) {
        return getVal(values);
      }

      return values.map((v) => getVal(v)).filter((v) => v !== null);
    },
    getReturnValue(key) {
      if (this.options.length === 0) {
        return this.localValue;
      }

      if (!this.multiple) {
        if (this.clearable && this.keyIsSelected(key)) {
          return null;
        }

        const val = this.valueByKey(key);
        if (!this.returnObject) {
          return val.value;
        }

        return this.localItems.filter(
          (i) => this.base64(i[this.valueKey]) === key,
        )[0];
      }

      if (this.keyIsSelected(key)) {
        const val = this.valueByKey(key);
        if (!this.returnObject) {
          return this.localValue.filter((v) => v !== val.value);
        }

        return this.localValue.filter((v) => v[this.valueKey] !== val.value);
      }

      if (!this.returnObject) {
        const val = [...this.localValue];
        val.push(
          ...this.options.filter((i) => i.key === key).map((i) => i.value),
        );
        return val;
      }

      const val = [...this.localValue];
      val.push(
        ...this.localItems.filter((i) => this.base64(i[this.valueKey]) === key),
      );
      return val;
    },
    keyIsSelected(key) {
      if (this.valueIsEmpty) {
        return false;
      }

      if (this.val === null) {
        return false;
      }

      return this.val === key || this.val.includes(key);
    },
    emitItemClick(key) {
      let returnVal = this.getReturnValue(key);
      if (typeof returnVal === 'undefined') {
        returnVal = null;
      }
      this.$emit('item-click', returnVal);
    },
    updateVal(key) {
      let returnVal = this.getReturnValue(key);
      if (typeof returnVal === 'undefined') {
        returnVal = null;
      }
      this.$emit('input', returnVal);
      this.$emit('update:value', returnVal);
      this.$emit('change', returnVal);
      if (!this.controlled) {
        this.localValue = copy(returnVal);
      }
    },
    scrollFocusedItemIntoView() {
      if (this.focusedItem < 0) {
        return;
      }
      this.$nextTick(() => {
        const container = this.$refs.body;
        if (!container) {
          return;
        }
        const focusedOption = this.filteredOptions[this.focusedItem];
        if (!focusedOption) {
          return;
        }
        const focusedElement = container.querySelector(`[data-key="${focusedOption.key}"]`);
        if (focusedElement) {
          focusedElement.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    moveFocusDown() {
      if (this.focusedItem === this.filteredOptions.length - 1) {
        return;
      }

      this.focusedItem += 1;
      if (this.focusedItem >= this.amountOfItems) {
        this.amountOfItems = this.focusedItem + 1;
      }
      this.scrollFocusedItemIntoView();
    },
    moveFocusUp() {
      if (this.focusedItem === 0) {
        return;
      }

      this.focusedItem -= 1;
      this.scrollFocusedItemIntoView();
    },
    handleKeyDown(event) {
      if (event.key === 'Enter') {
        if (this.filteredOptions.length === 0) {
          return;
        }
        this.selectFocusedItem();
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.code === 'ArrowDown' || event.which === 40) {
        event.preventDefault();
        event.stopPropagation();
        this.moveFocusDown();
        return;
      }

      if (event.code === 'ArrowUp' || event.which === 38) {
        event.preventDefault();
        event.stopPropagation();
        this.moveFocusUp();
      }
    },
  },
  watch: {
    search() {
      this.focusedItem = 0;
    },
    open(value) {
      if (this.showMenu === value) {
        return;
      }
      if (!value) {
        this.close();
        return;
      }
      this.show();
    },
    showMenu(value) {
      this.$emit('update:open', value);
    },
    items() {
      if (this.cacheItems) {
        this.localItems = uniqBy(
          [...this.localItems, ...this.calculatedItems],
          (el) => el[this.valueKey],
        );
        return;
      }

      this.localItems = this.calculatedItems;
    },
    value() {
      if (stringify(this.localValue) === stringify(this.value)) {
        return;
      }
      this.localValue = copy(this.value);
    },
    loading() {
      this.loaded = true;
    },
    filteredOptions(val) {
      if (this.focusedItem === 0) {
        return;
      }
      if (val.length === 0) {
        this.focusedItem = 0;
        return;
      }
      if (val.length - 1 < this.focusedItem) {
        this.focusedItem = this.filteredOptions.length - 1;
      }
    },
  },
  mounted() {
    if (!this.loading) {
      this.loaded = true;
    }
    if (this.autoFocus) {
      this.show();
    }
  },
  created() {
    if (this.open) {
      this.show();
    }
    this.localValue = copy(this.value);
    this.localItems = this.calculatedItems;
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  @import "shared/assets/scss/box-shadow";

  ._trigger {
    ._value {
      display: flex;
      align-items: baseline;

      &.-full-width {
        max-width: 100%;
      }

      ._label {
        margin-right: .4rem;
      }
    }
  }

  ._input-field {
    align-items: center;
    width: 100%;
    border-radius: $input-field-border-radius;

    &:focus {
      outline: none;
    }

    ._input {
      background-color: map_get($grey, "lighten-6");

      ._search {
        display: flex;
        flex: 1 1 10rem;
        align-items: center;
        padding-left: .4rem;
        margin-top: -.5rem;
        background-color: inherit;
        border: none;

        ._search-input {
          border-radius: 0;
        }

        &:focus {
          outline: none;
        }
      }
    }

    &.-tags,
    &.-search {
      padding: .6rem;
    }

    &:not(:first-child).-tags,
    &:not(:first-child).-search {
      padding-top: 0;
    }

    &.-popup {
      padding: 0;
      border-bottom: 1px solid $border-color;
      border-radius: 0;

      ._input {
        background-color: map_get($grey, "lighten-5");
        border-radius: 0;
      }
    }
  }

  .m-select {
    border-radius: $input-field-border-radius;

    ._trigger {
      min-height: $input-height-default;

      &.-small {
        min-height: $input-height-sm;
      }

      &.-xs {
        min-height: $xs-input-height;
      }
    }
  }

  ._m-select-overlay {
    ._body {
      max-height: 30rem;
      padding: .4rem 0;
      overflow: auto;
    }

    ._no-items {
      color: $font-color-secondary;
    }

    &.-mobile {
      ._input {
        margin-bottom: 2rem;
        background-color: white;
        border-radius: 0;

        &.-popup {
          padding: .8rem;
        }
      }
    }
  }
</style>
